<script setup lang="ts">
import { ref, onMounted } from "vue";
import { bsonId } from "@/common/utils/common";

import Quill from "quill";
import "quill/dist/quill.snow.css";

const props = defineProps<{
  modelValue: string;
}>();

const emit = defineEmits(["update:modelValue", "blur"]);

const editorContainer = ref<HTMLDivElement | null>(null);
const started = ref(false);
const id = ref(bsonId());

onMounted(async () => {
  if (editorContainer.value && !started.value) {
    const SizeStyle = Quill.import("attributors/style/size");
    SizeStyle.whitelist = [
      "0.5rem",
      "0.625rem",
      "0.75rem",
      "0.875rem",
      "1rem",
      "1.125rem",
      "1.25rem",
      "1.5rem",
      "1.75rem",
      "2rem",
      "2.5rem",
    ];
    Quill.register(SizeStyle, true);

    const quill = new Quill(editorContainer.value, {
      theme: "snow",
      modules: {
        toolbar: `#toolbar-${id.value}`,
      },
    });

    await new Promise((resolve) => setTimeout(resolve, 150));

    quill.clipboard.dangerouslyPasteHTML(
      0,
      props.modelValue?.replaceAll("\\", "")
    );
    quill.blur();
    started.value = true;

    quill.on("selection-change", (range) => {
      if (!range) {
        emit("blur");
      }
    });

    quill.on("text-change", () => {
      const content = quill.root.innerHTML;

      emit("update:modelValue", content);
    });
  }
});
</script>

<template>
	<section class="base-text-editor">
		<template v-if="$slots.label">
			<div class="label">
				<slot name="label" />
			</div>
		</template>

		<div class="textarea-editor">
			<div :id="`toolbar-${id}`">
				<!-- Cabeçalhos -->
				<select class="ql-header">
					<option value="1">Título 1</option>
					<option value="2">Título 2</option>
					<option value="3">Título 3</option>
					<option value="4">Título 4</option>
					<option selected>Normal</option>
				</select>

				<!-- Tamanhos personalizados -->
				<select class="ql-size">
					<option value="0.5rem">8px</option>
					<option value="0.625rem">10px</option>
					<option value="0.75rem">12px</option>
					<option value="0.875rem">14px</option>
					<option value="1rem" selected>16px</option>
					<option value="1.125rem">18px</option>
					<option value="1.25rem">20px</option>
					<option value="1.5rem">24px</option>
					<option value="1.75rem">28px</option>
					<option value="2rem">32px</option>
					<option value="2.5rem">40px</option>
				</select>

				<!-- Estilos -->
				<button class="ql-bold"></button>
				<button class="ql-italic"></button>
				<button class="ql-underline"></button>
				<button class="ql-strike"></button>

				<!-- Cores -->
				<select class="ql-color"></select>
				<select class="ql-background"></select>

				<!-- Links e fórmulas -->
				<button class="ql-link"></button>
				<!-- <button class="ql-formula"></button> -->

				<!-- Limpar formatação -->
				<button class="ql-clean"></button>
			</div>
			<div ref="editorContainer"></div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
.base-text-editor {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .label {
    font-size: 0.875rem;
    margin-block-end: 0.2rem;
    margin-inline: 0.5rem;
  }

  .textarea-editor {
    display: flex;
    flex-direction: column;
    width: 100%;
    border: 1px solid var(--background);
    border-radius: 0.5rem;
    padding: 0.5rem;
  }

  :deep(.ql-container) {
    border: none;
  }

  :deep(.ql-editor) {
    min-height: 9rem;
    font-size: 1rem;
  }
}
</style>
